import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  MailCheck,
  LayoutDashboard,
  User,
  LogOut,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { logout } from "../../store/authSlice";
import Images from "../../utils/Images";

export default function AuthenticatedFooter() {
  const currentYear = new Date().getFullYear();
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/signin");
  };

  const footerLinks = {
    dashboard: [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { name: "New Registration", href: "/new-registration" },
      { name: "My Clients", href: "/my-clients" },
      { name: "Commission", href: "/commission" },
    ],
    legal: [
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" },
    ],
  };

  const socialLinks = [
    { name: "Facebook", icon: Facebook, href: "#" },
    { name: "Twitter", icon: Twitter, href: "#" },
    { name: "Instagram", icon: Instagram, href: "#" },
    { name: "LinkedIn", icon: Linkedin, href: "#" },
    { name: "YouTube", icon: Youtube, href: "#" },
  ];

  return (
    <footer className="relative text-white bg-gradient-to-br via-gray-900 from-slate-900 to-slate-800">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.15)_1px,transparent_0)] bg-[size:20px_20px]"></div>
      </div>
      
      <div className="relative px-6 py-20 mx-auto max-w-7xl lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          
          {/* Brand Section */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img src={Images.logo_white} alt="TripSecure" className="mb-6 w-48" />
            
            <p className="mb-8 max-w-md text-lg leading-relaxed text-gray-300">
              Empowering insurance agents with cutting-edge tools, comprehensive training, 
              and competitive commissions to build successful careers in the insurance industry.
            </p>

            {/* User Info Card */}
            {user && (
              <motion.div 
                className="p-4 mb-6 rounded-lg border backdrop-blur-sm bg-white/5 border-white/10"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="flex items-center mb-3 space-x-3">
                  <div className="flex justify-center items-center w-10 h-10 rounded-full bg-emerald-500/20">
                    <User size={18} className="text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                      {user.first_name || user.firstName} {user.last_name || user.lastName}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {user.email || user.email_address}
                    </p>
                  </div>
                </div>
                {user.agent_code && (
                  <p className="text-xs text-gray-400">
                    Agent Code: <span className="font-semibold text-emerald-400">{user.agent_code}</span>
                  </p>
                )}
              </motion.div>
            )}

            {/* Contact Cards */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <motion.div 
                className="flex items-center p-3 space-x-3 rounded-lg border backdrop-blur-sm transition-all duration-300 bg-white/5 border-white/10 hover:bg-white/10"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-emerald-500/20 shrink-0">
                  <MailCheck size={16} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs tracking-wide text-gray-400 uppercase">Email</p>
                  <p className="text-sm font-medium text-white break-all">agents@tripsecure.com</p>
                </div>
              </motion.div>
              
              <motion.div 
                className="flex items-center p-3 space-x-3 rounded-lg border backdrop-blur-sm transition-all duration-300 bg-white/5 border-white/10 hover:bg-white/10"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-emerald-500/20 shrink-0">
                  <Phone size={16} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs tracking-wide text-gray-400 uppercase">Phone</p>
                  <p className="text-sm font-medium text-white break-all">+233 20 123 4567</p>
                </div>
              </motion.div>
              
              <motion.div 
                className="flex items-center p-3 space-x-3 rounded-lg border backdrop-blur-sm transition-all duration-300 bg-white/5 border-white/10 hover:bg-white/10"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-emerald-500/20 shrink-0">
                  <MapPin size={16} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs tracking-wide text-gray-400 uppercase">Location</p>
                  <p className="text-sm font-medium text-white break-all">Accra, Ghana</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Navigation Links */}
          {Object.entries(footerLinks).map(([category, links], index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="space-y-6"
            >
              <div className="relative">
                <h3 className="mb-1 text-lg font-semibold text-white capitalize">
                  {category}
                </h3>
                <div className="w-12 h-0.5 bg-gradient-to-r from-emerald-500 to-emerald-300"></div>
              </div>
              
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => navigate(link.href)}
                      className="flex items-center w-full text-left text-gray-300 transition-all duration-300 group hover:text-white"
                    >
                      {link.icon && <link.icon size={16} className="mr-2" />}
                      <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 mr-0 group-hover:mr-3 transition-all duration-300 rounded-full"></span>
                      <span className="transition-transform duration-300 group-hover:translate-x-1">
                        {link.name}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="my-12">
          <div className="w-full h-px bg-gradient-to-r from-transparent to-transparent via-white/20"></div>
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex flex-col justify-between items-center space-y-6 lg:flex-row lg:space-y-0"
        >
          {/* Copyright */}
          <div className="text-center lg:text-left">
            <p className="font-medium text-gray-400">
              © {currentYear} <span className="font-semibold text-white">TripSecure</span>. All rights reserved.
            </p>
          </div>

          {/* Actions and Social Links */}
          <div className="flex flex-col items-center space-y-4 lg:flex-row lg:space-y-0 lg:space-x-6">
            {/* Logout Button */}
            <motion.button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 space-x-2 text-sm font-medium text-gray-300 rounded-lg border backdrop-blur-sm transition-all duration-300 bg-white/5 border-white/10 hover:text-white hover:bg-red-500/20 hover:border-red-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogOut size={16} />
              <span>Logout</span>
            </motion.button>

            {/* Social Links */}
            <div className="flex items-center space-x-2">
              <span className="mr-4 text-sm text-gray-400">Follow us</span>
              <div className="flex space-x-3">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    className="flex justify-center items-center w-10 h-10 text-gray-400 rounded-full border backdrop-blur-sm transition-all duration-300 group bg-white/5 border-white/10 hover:text-white hover:bg-emerald-500/20 hover:border-emerald-500/30"
                    aria-label={social.name}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon size={18} />
                  </motion.a>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
